package org.multipaz.crypto

import kotlinx.io.bytestring.ByteString
import kotlinx.io.bytestring.append
import kotlinx.io.bytestring.buildByteString
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put
import org.multipaz.securearea.KeyInvalidatedException
import org.multipaz.securearea.KeyLockedException
import org.multipaz.util.appendInt32
import org.multipaz.util.deflate
import org.multipaz.util.fromBase64Url
import org.multipaz.util.inflate
import org.multipaz.util.toBase64Url
import kotlin.coroutines.cancellation.CancellationException
import kotlin.math.ceil
import kotlin.random.Random

/**
 *  JSON Web Encryption (JWE) support routines
 */
object JsonWebEncryption {
    /**
     * Encrypts a claim set using ECDH-ES.
     *
     * Reference: [RFC 7518 Section 4.6 Key Agreement with Elliptic Curve Diffie-Hellman Ephemeral Static (ECDH-ES)](https://datatracker.ietf.org/doc/html/rfc7518#section-4.6)
     *
     * @param claimsSet the claims set to encrypt.
     * @param recipientPublicKey the public key to encrypt to.
     * @param encAlg the encryption algorithm, [Algorithm.A128GCM], [Algorithm.A192GCM], or [Algorithm.A256GCM].
     * @param apu agreement PartyUInfo (apu) parameter or `null`.
     * @param apv agreement PartyVInfo (apv) parameter or `null`.
     * @param random the [Random] used to generate a nonce.
     * @param kid if not `null`, this will be included as the value for the `kid` parameter in the header.
     * @param compressionLevel The compression level to use for DEFLATE compression or `null` to not compress.
     * @return the compact serialization of the JWE.
     * @throws IllegalArgumentException if the encryption algorithm is not supported.
     */
    @Throws(
        IllegalArgumentException::class,
        CancellationException::class
    )
    suspend fun encrypt(
        claimsSet: JsonObject,
        recipientPublicKey: EcPublicKey,
        encAlg: Algorithm,
        apu: ByteString?,
        apv: ByteString?,
        random: Random = Crypto.secureRandom,
        kid: String? = null,
        compressionLevel: Int? = null
    ): String {
        val keyDataLenBits = when (encAlg) {
            Algorithm.A128GCM -> 128
            Algorithm.A192GCM -> 192
            Algorithm.A256GCM -> 256
            else -> throw IllegalArgumentException("encAlg $encAlg not supported")
        }

        return Crypto.createEcPrivateKey(recipientPublicKey.curve).use { senderEphemeralKey ->
            val protectedHeader = buildJsonObject {
                put("alg", "ECDH-ES")
                put("enc", encAlg.joseAlgorithmIdentifier)
                apu?.let { put("apu", it.toByteArray().toBase64Url()) }
                apv?.let { put("apv", it.toByteArray().toBase64Url()) }
                put("epk", senderEphemeralKey.publicKey.toJwk())
                kid?.let { put("kid", it) }
                if (compressionLevel != null) {
                    put("zip", "DEF")
                }
            }
            val protectedHeaderB64 = Json.encodeToString(protectedHeader).encodeToByteArray().toBase64Url()

            val algId = encAlg.joseAlgorithmIdentifier!!.encodeToByteArray()
            val contentEncryptionKey = Crypto.keyAgreement(senderEphemeralKey, recipientPublicKey).use { sharedSecret ->
                concatKDF(
                    sharedSecretZ = sharedSecret,
                    keyDataLenBits = keyDataLenBits,
                    algorithmId = buildByteString { appendInt32(algId.size); append(algId) },
                    partyUInfo =  buildByteString { val info = apu ?: ByteString(); appendInt32(info.size); append(info) },
                    partyVInfo =  buildByteString { val info = apv ?: ByteString(); appendInt32(info.size); append(info) },
                    suppPubInfo = buildByteString { appendInt32(keyDataLenBits) }
                )
            }
            // 96 bits (12 bytes) is a recommended IV size, but AndroidOpenSSL provider requires
            // it, so just go with that recommendation.
            val nonce = random.nextBytes(12)
            val uncompressed = Json.encodeToString(claimsSet).encodeToByteArray()
            val messageToEncrypt = if (compressionLevel != null) {
                uncompressed.deflate(compressionLevel)
            } else {
                uncompressed
            }
            val cipherTextWithTag = contentEncryptionKey.use { cek ->
                Crypto.encrypt(
                    algorithm = encAlg,
                    key = cek,
                    nonce = nonce,
                    messagePlaintext = messageToEncrypt,
                    aad = protectedHeaderB64.encodeToByteArray(),
                )
            }
            // Auth tag is a single block which is always 16 bytes long for AES, irrespective of
            // the key length.
            val cipherText = cipherTextWithTag.copyOfRange(0, cipherTextWithTag.size - 16)
            val authTag = cipherTextWithTag.copyOfRange(cipherTextWithTag.size - 16, cipherTextWithTag.size)
            protectedHeaderB64 + "." + "." + nonce.toBase64Url() + "." + cipherText.toBase64Url() + "." + authTag.toBase64Url()
        }
    }

    /**
     * Decrypts an encrypted JWT using ECDH-ES.
     *
     * Reference: [RFC 7518 Section 4.6 Key Agreement with Elliptic Curve Diffie-Hellman Ephemeral Static (ECDH-ES)](https://datatracker.ietf.org/doc/html/rfc7518#section-4.6)
     *
     * @param encryptedJwt the compact serialization of the JWE.
     * @param recipientKey the recipients private key corresponding to the public key this was encrypted to.
     * @return the decrypted claims set.
     * @throws IllegalArgumentException if the JWE format or algorithm is invalid or unsupported.
     * @throws IllegalStateException if decryption fails.
     * @throws KeyLockedException if the key needs unlocking.
     * @throws KeyInvalidatedException if the key is no longer usable.
     */
    @Throws(
        IllegalArgumentException::class,
        IllegalStateException::class,
        KeyLockedException::class,
        KeyInvalidatedException::class,
        CancellationException::class
    )
    suspend fun decrypt(
        encryptedJwt: String,
        recipientKey: AsymmetricKey
    ): JsonObject {
        val splits = encryptedJwt.split(".")
        require(splits.size == 5)
        val (protectedHeaderB64, encryptedKeyB64, ivB64, cipherTextB64, authenticationTagB64) = splits
        val protectedHeader = Json.decodeFromString(JsonObject.serializer(), protectedHeaderB64.fromBase64Url().decodeToString())

        require(protectedHeader["alg"]!!.jsonPrimitive.content == "ECDH-ES")

        val encAlg = Algorithm.fromJoseAlgorithmIdentifier(protectedHeader["enc"]!!.jsonPrimitive.content)
        val keyDataLenBits = when (encAlg) {
            Algorithm.A128GCM -> 128
            Algorithm.A192GCM -> 192
            Algorithm.A256GCM -> 256
            else -> throw IllegalArgumentException("encAlg $encAlg not supported")
        }

        val senderEphemeralKey = EcPublicKey.fromJwk(protectedHeader["epk"]!!.jsonObject)

        val apu = ByteString(protectedHeader["apu"]?.jsonPrimitive?.content?.fromBase64Url() ?: byteArrayOf())
        val apv = ByteString(protectedHeader["apv"]?.jsonPrimitive?.content?.fromBase64Url() ?: byteArrayOf())

        val algId = encAlg.joseAlgorithmIdentifier!!.encodeToByteArray()
        val contentEncryptionKey = recipientKey.keyAgreement(senderEphemeralKey).use { sharedSecret ->
            concatKDF(
                sharedSecretZ = sharedSecret,
                keyDataLenBits = keyDataLenBits,
                algorithmId = buildByteString { appendInt32(algId.size); append(algId) },
                partyUInfo =  buildByteString { appendInt32(apu.size); append(apu) },
                partyVInfo =  buildByteString { appendInt32(apv.size); append(apv) },
                suppPubInfo = buildByteString { appendInt32(keyDataLenBits) }
            )
        }
        val clearText = contentEncryptionKey.use { cek ->
            Crypto.decrypt(
                algorithm = encAlg,
                key = cek,
                nonce = ivB64.fromBase64Url(),
                aad = protectedHeaderB64.encodeToByteArray(),
                messageCiphertext = cipherTextB64.fromBase64Url() + authenticationTagB64.fromBase64Url()
            )
        }

        val compressionAlgorithm = protectedHeader["zip"]?.jsonPrimitive?.content
        if (compressionAlgorithm == null) {
            return Json.decodeFromString(JsonObject.serializer(), clearText.decodeToString())
        }
        when (compressionAlgorithm) {
            "DEF" -> {
                return Json.decodeFromString(
                    deserializer = JsonObject.serializer(),
                    string = clearText.inflate().decodeToString()
                )
            }
            else -> throw IllegalArgumentException("Unsupported compression algorithm $compressionAlgorithm")
        }
    }

    /**
     * Implements Concat KDF as specified in RFC 7518, Section 4.6.2.
     *
     * KDF(Z, keydatalen, AlgorithmID, PartyUInfo, PartyVInfo, SuppPubInfo, SuppPrivInfo)
     * For ECDH-ES+AxxxKW, SuppPrivInfo is empty.
     * For ECDH-ES, this KDF is used to derive the KEK for AES Key Wrap.
     */
    internal suspend fun concatKDF(
        sharedSecretZ: SecureByteString,
        keyDataLenBits: Int, // Desired output key length in bits (e.g., 128, 192, 256 for AES Key Wrap)
        algorithmId: ByteString,
        partyUInfo: ByteString,
        partyVInfo: ByteString,
        suppPubInfo: ByteString // e.g., keyDataLenBits as a 32-bit big-endian integer
    ): SecretKey {
        val keyDataLenBytes = keyDataLenBits / 8
        val reps = ceil(keyDataLenBytes.toDouble() / 32.0).toInt() // Assuming SHA-256 (32 bytes output)
        var round = 1

        val ssBytes = sharedSecretZ.encoded
        try {
            val derivedKey = buildByteString {
                var derivedKeySize = 0
                while (derivedKeySize < keyDataLenBytes && round <= reps) {
                    val toDigest = buildByteString {
                        appendInt32(round)
                        append(ssBytes)
                        append(algorithmId)
                        append(partyUInfo)
                        append(partyVInfo)
                        append(suppPubInfo)
                    }
                    // SuppPrivInfo is omitted for ECDH-ES+AxxxKW as per RFC 7518
                    append(Crypto.digest(Algorithm.SHA256, toDigest.toByteArray()))
                    derivedKeySize += 32
                    round++
                }
            }
            val keyBytes = derivedKey.toByteArray(startIndex = 0, endIndex = keyDataLenBytes)
            val result = SecretKey(keyBytes)
            keyBytes.secureZero()
            return result
        } finally {
            ssBytes.secureZero()
        }
    }

}